import React, { useState, useEffect } from 'react'
import { View, Pressable } from 'react-native'
import { Calendar, LocaleConfig } from 'react-native-calendars'

import PropTypes from 'prop-types'
import moment from 'moment'

import { VFText, Icon, Shimmer } from '@vfgroup-oneplatform/foundation/Components'
import { useTheme } from '@vfgroup-oneplatform/foundation/Components/Themes'
import { DateCurrencyFromatter } from '@vfgroup-oneplatform/foundation/Utils'

import { getThemeImages } from '../../Themes'

import { timeSlotsLoadingConfig, shimmerPlaceholderConfig, calendarLoadingConfig } from './AppointmentCalendar.LoadingConfig'

import style from './AppointmentCalendar.styles'


LocaleConfig.locales.al = {
  monthNames: [
    'Janar',
    'Shkurt',
    'Mars',
    'Prill',
    'Maj',
    'Qershor',
    'Korrik',
    'Gusht',
    'Shtator',
    'Tetor',
    'Nëntor',
    'Dhjetor',
  ],
  monthNamesShort: [
    'Jan',
    'Shku',
    'Mar',
    'Pri',
    'Maj',
    'Qer',
    'Korr',
    'Gush',
    'Sht',
    'Tet',
    'Nën',
    'Dhj',
  ],
  dayNames: ['E dielë', 'E hënë', 'E martë', 'E mërkurë', 'E enjte', 'E premte', 'E shtunë'],
  dayNamesShort: ['D', 'H', 'M', 'M', 'E', 'P', 'Sh'],
  today: 'Sot',
}

LocaleConfig.locales.en = {
  monthNames: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  monthNamesShort: [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sept',
    'Oct',
    'Nov',
    'Dec',
  ],
  dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  dayNamesShort: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  today: 'Today',
}


moment.updateLocale('sq', {
  longDateFormat: {
    LLLL: 'dddd, D MMMM YYYY',
  },
  weekdays: 'E diel_E hënë_E martë_E mërkurë_E enjte_E premte_E shtunë'.split(
    '_'
  ),
})

moment.updateLocale('en', {
  longDateFormat: {
    LLLL: 'dddd, D MMMM YYYY',
  }
})

const { dateFormats, dateTransformer } = DateCurrencyFromatter.dateCurrencyFromatter

const AppointmentCalendar = ({
  onDateOrTimeChange,
  withFullMonthName,
  getAvailableTimeSlots,
  store,
  getAppointments
}) => {

  const [selectedDate, setSelectedDate] = useState(moment())
  const [selectedTime, setSelectedTime] = useState('')

  const [timeSlots, setTimeSlots] = useState([])
  const [timeSlotsLoading, setTimeSlotsLoading] = useState(false)

  const [isCalendarLoading, setIsCalendarLoading] = useState(false)
  const [occupiedTimeSlots, setOccupiedTimeSlots] = useState([])

  const theme = useTheme()
  const Images = getThemeImages(theme.name)


  const getAllAppointments = () => {

    const params = {
      'place.id': store.id,
    }

    setIsCalendarLoading(true)

    getAppointments(params, {
      onSuccess: (res) => {
        setIsCalendarLoading(false)
        const formattedRes = []
        if (res.length > 0) {
          res.map(item => {
            formattedRes.push({
              date: item?.date?.selectedDate,
              time: item?.date?.selectedTime
            })
          })
        }
        setOccupiedTimeSlots(formattedRes)
      },
      onError: () => {
        setIsCalendarLoading(false)
      }
    })
  }


  useEffect(() => {
    getAllAppointments()
  }, [])


  useEffect(() => {
    onDateOrTimeChange({ selectedDate, selectedTime })
  }, [selectedDate, selectedTime])

  useEffect(() => {
    setTimeSlotsLoading(true)
    setTimeSlots([])
    getAvailableTimeSlots(selectedDate, store.id, {
      onSuccess: res => {
        setTimeSlotsLoading(false)
        setTimeSlots(res)
      },
      onError: () => setTimeSlotsLoading(false)
    })
  }, [selectedDate])


  const renderTimeSlots = () => {
    return (
      <>
        {timeSlotsLoading ? <Shimmer
          config={timeSlotsLoadingConfig}
          shimmerPlaceholderConfig={shimmerPlaceholderConfig}
        /> : <>
          {timeSlots?.length === 0 ? <VFText
            i18nKey='book_appointment_timeslots_no_available'
            style={style.noTimeSlots}
          /> : <>
            {timeSlots?.map((el, index) => {
              const { startTime, endTime } = el?.timeSlots?.[0]
              const timeSlot = startTime + ' - ' + endTime

              const endDate = `${selectedDate.format("YYYY")}-${selectedDate.format("MM")}-${selectedDate.format("DD")}T${endTime}:00`

              if (!moment().isBefore(endDate)) {
                return null
              }

              const occupiedTimeSlotsForSelectedDate = occupiedTimeSlots?.filter(item => moment(item?.date, 'YYYY-MM-DDTHH:mm:ss.000Z').format('DD/MM/YYYY') == moment(selectedDate, 'DD/MM/YYYY').format('DD/MM/YYYY')) || []

              if (occupiedTimeSlotsForSelectedDate.find(item => item?.time === timeSlot)) {
                return null
              }

              return (
                <View style={style.timeSlotContainerStyle} key={index}>
                  <VFText
                    i18nKey={timeSlot}
                    style={style.timeSlotStyle(selectedTime === timeSlot, null, theme)}
                    onPress={() => {
                      setSelectedTime(timeSlot)
                    }}
                    suppressHighlighting={true}
                  />
                </View>
              )
            })}
          </>}
        </>}
      </>
    )
  }

  return (
    <View>
      <View style={style.cardContentStyle}>
        {isCalendarLoading ? <Shimmer
          config={calendarLoadingConfig}
          shimmerPlaceholderConfig={shimmerPlaceholderConfig}
        /> : <>
          <Calendar
            style={style.calendarStyle}
            theme={{
              calendarBackground: theme.colors.cardBackgroundColor,
              ...style.monthTitleStyle(theme, withFullMonthName),
            }}
            minDate={dateTransformer(moment(), dateFormats.calendarFormat)}
            maxDate={null}
            monthFormat={withFullMonthName ? 'MMMM yyyy' : 'MMM yyyy'}
            hideExtraDays={true}
            firstDay={1}
            disableAllTouchEventsForDisabledDays={true}
            markingType={'custom'}
            renderArrow={direction => (
              <Icon
                name={
                  direction === 'right'
                    ? Images.ic_chevron_right_black_white
                    : Images.ic_chevron_left_black_white
                }
                type="image"
                style={style.calendarArrowStyle(direction)}
              />
            )}
            dayComponent={({ date, marking, onPress, onLongPress, state }) => {
              const momentDate = moment(date.timestamp)
              const momentSelectedDate = moment(selectedDate)

              const dayOfWeek = momentDate.isoWeekday()
              return (
                <Pressable
                  onPress={() => {
                    setSelectedDate(momentDate)
                    setSelectedTime('')
                  }}
                  style={[
                    style.dateContainer,
                    momentDate.isSame(moment(), 'day') && style.todayDateContainer,
                    momentDate.isSame(momentSelectedDate, 'day') && style.selectedContainer,
                  ]}
                  disabled={momentDate.isBefore(moment(), 'day') || state === 'disabled' || dayOfWeek === 7 || dayOfWeek === 6}
                >
                  <VFText
                    i18nKey={date?.day?.toString()}
                    style={[
                      style.dateText(theme),
                      momentDate.isBefore(moment(), 'day') && style.disabledDaysNumbersStyles(theme),
                      momentDate.isSame(momentSelectedDate, 'day') && style.selectedDate,
                      (state === 'disabled' || dayOfWeek === 7 || dayOfWeek === 6) && style.disabledDaysNumbersStyles(theme),
                    ]}
                  />
                </Pressable>
              )
            }}
          />
          <VFText
            i18nKey='book_appointment_date_available_slots'
            style={style.textStyle(theme)}
          />
          <VFText
            i18nKey={!selectedDate ? '' : moment(selectedDate, 'DD/MM/YYYY').format('LLLL')}
            style={style.selectedDateTextStyle(theme)}
          />
          <View style={style.timeSlotsContainerStyle}>
            {renderTimeSlots()}
          </View>
        </>}
      </View>
    </View>
  )
}

AppointmentCalendar.defaultProps = {
  onDateOrTimeChange: () => { },
  withFullMonthName: true,
}

AppointmentCalendar.propTypes = {
  onDateOrTimeChange: PropTypes.func,
  withFullMonthName: PropTypes.bool,
  store: PropTypes.object
}

export default AppointmentCalendar
